import React, { useState, useEffect } from 'react';
import { X, Globe, Sparkles, ChevronRight, Check, Plus, Folder } from 'lucide-react';
import { 
  getAvailableLanguages, 
  getTemplateTypesForLanguage, 
  getTemplate,
  getLanguageName,
  getLanguageFlag 
} from '../utils/seedTemplates';
import { apiService } from '../services/apiService';
import { useLanguage } from '../contexts/LanguageContext';

const SmartSeedModal = ({ isOpen, onClose, onItemsSeeded }) => {
  const [step, setStep] = useState(1); // 1: Language, 2: Template, 3: Categories, 4: Completion
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [categoryMapping, setCategoryMapping] = useState({}); // Maps template categories to user categories
  const [userCategories, setUserCategories] = useState([]);
  const [newCategories, setNewCategories] = useState([]); // Categories to be created
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedingProgress, setSeedingProgress] = useState(0);
  const [completionResult, setCompletionResult] = useState(null); // Store completion data
  const { t } = useLanguage();

  // Load user's existing categories
  useEffect(() => {
    if (isOpen && step === 3) {
      loadUserCategories();
    }
  }, [isOpen, step]);

  const loadUserCategories = async () => {
    try {
      const response = await apiService.getUserCategories();
      const cats = Array.isArray(response) ? response : (response.categories || []);
      setUserCategories(cats);
      console.log('Loaded user categories for Smart Seed:', cats);
    } catch (error) {
      console.error('Failed to load categories:', error);
      // Set empty array as fallback
      setUserCategories([]);
    }
  };

  // Reset modal state when closed
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedLanguage(null);
      setSelectedTemplate(null);
      setCategoryMapping({});
      setNewCategories([]);
      setCompletionResult(null);
      setIsSeeding(false);
      setSeedingProgress(0);
    }
  }, [isOpen]);

  // Add ESC key listener
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language);
    setStep(2);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setStep(3);
    
    // Initialize category mapping
    const mapping = {};
    const newCats = [];
    
    template.categories.forEach(templateCat => {
      // Try to find an exact matching user category (no semantic matching)
      const matchingUserCat = userCategories.find(userCat => {
        // Handle both string categories and object categories
        const userCatName = typeof userCat === 'string' ? userCat : userCat.name;
        if (!userCatName) return false;
        
        // Only exact matches, no semantic matching
        return userCatName.toLowerCase() === templateCat.name.toLowerCase();
      });
      
      if (matchingUserCat) {
        // Handle both string categories and object categories
        const categoryValue = typeof matchingUserCat === 'string' ? matchingUserCat : matchingUserCat.id;
        mapping[templateCat.name] = categoryValue;
      } else {
        // Mark for creation
        newCats.push(templateCat.name);
        mapping[templateCat.name] = 'CREATE_NEW';
      }
    });
    
    setCategoryMapping(mapping);
    setNewCategories(newCats);
  };

  // Simple semantic matching for common category names
  const isSemanticMatch = (userCatName, templateCatName) => {
    if (!userCatName || !templateCatName) return false;
    
    const userLower = userCatName.toLowerCase();
    const templateLower = templateCatName.toLowerCase();
    
    const matches = {
      'food': ['kjøtt og fisk', 'meieri', 'frukt og grønt', 'meat & fish', 'dairy', 'fruit & vegetables'],
      'groceries': ['dagligvarer', 'mat', 'food'],
      'cleaning': ['rengjøring', 'hygiene', 'cleaning & personal care'],
      'drinks': ['drikke', 'beverages']
    };
    
    for (const [key, values] of Object.entries(matches)) {
      if (userLower.includes(key) && values.some(v => templateLower.includes(v))) {
        return true;
      }
    }
    
    return false;
  };

  const updateCategoryMapping = (templateCatName, action, value) => {
    setCategoryMapping(prev => ({
      ...prev,
      [templateCatName]: action === 'existing' ? value : 'CREATE_NEW'
    }));
    
    if (action === 'new') {
      // Add to new categories if not already there
      if (!newCategories.includes(templateCatName)) {
        setNewCategories(prev => [...prev, templateCatName]);
      }
    } else {
      // Remove from new categories since user selected existing category
      setNewCategories(prev => prev.filter(cat => cat !== templateCatName));
    }
  };

  const handleSeedItems = async () => {
    if (!selectedTemplate) return;
    
    setIsSeeding(true);
    setSeedingProgress(0);
    
    try {
      // Prepare template items for database seeding
      const templateItems = [];
      for (const templateCategory of selectedTemplate.categories) {
        const categoryName = categoryMapping[templateCategory.name] === 'CREATE_NEW' 
          ? templateCategory.name  // Use the template category name for new categories
          : categoryMapping[templateCategory.name]; // Use the selected existing category name
        
        // Add items from this template category
        for (const itemName of templateCategory.items) {
          templateItems.push({
            name: itemName,
            category: categoryName
          });
        }
      }
      
      console.log(`✨ Seeding ${templateItems.length} items into database...`);
      setSeedingProgress(25);
      
      // Seed items into user's item history database (not the current list)
      const response = await apiService.seedItemDatabase(templateItems);
      setSeedingProgress(75);
      
      console.log(`✨ Smart Seed Response:`, response);
      console.log(`✨ Successfully seeded ${response.itemsAdded} new items into database`);
      console.log(`📊 Processed ${response.totalProcessed} total items`);
      
      if (response.categoriesAdded > 0) {
        console.log(`📁 Added ${response.categoriesAdded} new categories:`, response.newCategories);
      } else {
        console.log(`📁 No new categories were added`);
      }
      
      setSeedingProgress(100);
      
      // Show completion message with Quick Add tip
      setTimeout(() => {
        setStep(4); // Move to completion step
        
        // Store the result for when user clicks Done
        setCompletionResult({
          categoriesAdded: response.categoriesAdded,
          newCategories: response.newCategories,
          itemsAdded: response.itemsAdded,
          totalProcessed: response.totalProcessed
        });
        // Don't call onItemsSeeded here - wait for user to click Done
      }, 500);
      
    } catch (error) {
      console.error('Failed to seed items into database:', error);
      alert(t('seedingError'));
      setSeedingProgress(0);
    } finally {
      setTimeout(() => {
        setIsSeeding(false);
        setSeedingProgress(0);
      }, 1000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal smart-seed-modal">
        <div className="modal-header">
          <h2>
            <Sparkles className="icon" />
            {t('smartSeedDatabase')}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <X />
          </button>
        </div>

        <div className="modal-content">
          {/* Step 1: Language Selection */}
          {step === 1 && (
            <div className="step-content step-language">
              <div className="step-inner">
                <h3><Globe className="icon" /> {t('selectContentLanguage')}</h3>
                <p>{t('chooseContentLanguageDesc')}</p>
                
                <div className="language-grid">
                {getAvailableLanguages().map(lang => (
                  <button
                    key={lang.code}
                    className="language-card"
                    onClick={() => handleLanguageSelect(lang.code)}
                  >
                    <span className="flag">{lang.flag}</span>
                    <span className="name">{lang.name}</span>
                    <ChevronRight className="arrow" />
                  </button>
                ))}
              </div>
              </div>
            </div>
          )}

          {/* Step 2: Template Selection */}
          {step === 2 && selectedLanguage && (
            <div className="step-content step-template">
              <div className="step-inner">
                <h3>
                  {getLanguageFlag(selectedLanguage)} {t('chooseTemplateType')}
                </h3>
                <p>{t('selectTemplateDesc')}</p>
                
                <div className="template-grid">
                {getTemplateTypesForLanguage(selectedLanguage).map(({ type, template }) => (
                  <button
                    key={type}
                    className="template-card"
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <span className="icon">{template.icon}</span>
                    <div className="details">
                      <h4>{template.name}</h4>
                      <p>{template.description}</p>
                      <span className="item-count">
                        {template.categories.reduce((sum, cat) => sum + cat.items.length, 0)} items
                      </span>
                    </div>
                    <ChevronRight className="arrow" />
                  </button>
                ))}
              </div>
              </div>
            </div>
          )}

          {/* Step 3: Category Mapping */}
          {step === 3 && selectedTemplate && (
            <div className="step-content step-category">
              <div className="step-inner">
                <h3><Folder className="icon" /> {t('categoryMapping')}</h3>
                <p>{t('categoryMappingDesc')}</p>
                
                <div className="current-list-info">
                <p><strong>{t('seedingDatabaseWith')}</strong> {selectedTemplate.name} ({selectedTemplate.categories.reduce((sum, cat) => sum + cat.items.length, 0)} items)</p>
              </div>
              
              <div className="category-mapping">
                {selectedTemplate.categories.map(templateCat => (
                  <div key={templateCat.name} className="category-map-item">
                    <div className="template-category">
                      <strong>{templateCat.name}</strong>
                      <span className="item-count">({templateCat.items.length} items)</span>
                    </div>
                    
                    <div className="mapping-options">
                      <select
                        value={categoryMapping[templateCat.name] || 'CREATE_NEW'}
                        onChange={(e) => {
                          const value = e.target.value;
                          updateCategoryMapping(
                            templateCat.name, 
                            value === 'CREATE_NEW' ? 'new' : 'existing',
                            value
                          );
                        }}
                      >
                        <option value="CREATE_NEW">{t('createNewCategory')}</option>
                        {userCategories.length === 0 ? (
                          <option disabled>{t('noExistingCategories')}</option>
                        ) : (
                          userCategories.map(category => {
                            // Handle both string categories and object categories
                            const categoryName = typeof category === 'string' ? category : category.name;
                            const categoryValue = typeof category === 'string' ? category : category.id;
                            return (
                              <option key={categoryValue || categoryName} value={categoryValue || categoryName}>
                                {t('useExistingCategory').replace('{0}', categoryName)}
                              </option>
                            );
                          })
                        )}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
              
              {newCategories.length > 0 && (
                <div className="new-categories-preview">
                  <h4>{t('newCategoriesToCreate')}</h4>
                  <div className="new-categories-list">
                    {newCategories.map(catName => (
                      <span key={catName} className="new-category-tag">
                        <Plus className="icon" />
                        {catName}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </div>
          )}

          {/* Step 4: Completion */}
          {step === 4 && (
            <div className="step-content step-completion">
              <div className="step-inner">
                <div className="completion-content">
                  <div className="success-icon">
                    <Check className="icon large" />
                  </div>
                  
                  <h3>{t('seedingComplete')}</h3>
                  
                  <p className="success-message">
                    {t('seedingSuccessMessage')}
                  </p>
                  
                  <div className="quick-add-tip">
                    <div className="tip-header">
                      <Sparkles className="icon" />
                      <strong>{t('quickTip')}</strong>
                    </div>
                    <p>
                      {t('quickAddTipMessage')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="step-indicator">
            {step <= 3 ? t('stepOf').replace('{0}', step).replace('{1}', '3') : t('complete')}
          </div>
          
          <div className="button-group">
            {step > 1 && step < 4 && (
              <button 
                className="btn btn-secondary" 
                onClick={() => setStep(step - 1)}
              >
                {t('back')}
              </button>
            )}
            
            {step < 3 ? (
              <button 
                className="btn btn-primary" 
                disabled={
                  (step === 1 && !selectedLanguage) ||
                  (step === 2 && !selectedTemplate)
                }
              >
                {t('next')}
              </button>
            ) : step === 3 ? (
              <div className="seed-button-section">
                <button 
                  className="btn btn-primary"
                  onClick={handleSeedItems}
                  disabled={isSeeding}
                >
                  {isSeeding ? (
                    <>
                      <Sparkles className="icon spinning" />
                      {t('seedingDatabase')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="icon" />
                      {t('seedDatabase')}
                    </>
                  )}
                </button>
                
                {isSeeding && (
                  <div className="progress-container">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${seedingProgress}%` }}
                      ></div>
                    </div>
                    <span className="progress-text">{seedingProgress}%</span>
                  </div>
                )}
              </div>
            ) : step === 4 ? (
              <button 
                className="btn btn-primary"
                onClick={() => {
                  // Trigger stats refresh if available
                  if (window.refreshUserStats) {
                    console.log('🔄 Triggering stats refresh after seeding...');
                    window.refreshUserStats();
                  }
                  
                  onItemsSeeded({ 
                    message: 'Database seeded successfully',
                    ...completionResult 
                  });
                  onClose();
                }}
              >
                <Check className="icon" />
                {t('done')}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartSeedModal;
